import { useEffect, useRef } from 'react'
import { APP_REDO_EVENT, APP_UNDO_EVENT } from '@/src/renderer/hooks/useUndoRouter'
import { lastTouchedUndoRegion, resetUndoRegion, touchUndoRegion } from '@/src/renderer/lib/undo'
import { isInsideTaskDetails } from '../../../utils/taskDetailsFocus'

interface UseCanvasHistoryShortcutOptions {
  /** Nur im Maus-Werkzeug des sichtbaren Tabs (TabStack-Falle). */
  enabled: boolean
  onUndo: () => void
  onRedo: () => void
}

/**
 * ⌘/Strg + Z nimmt den letzten Schritt im Canvas zurück, ⇧⌘Z stellt ihn
 * wieder her.
 *
 * **Am `window`, anders als die übrigen Canvas-Kürzel** — und der Grund ist
 * das Löschen: Danach ist der Node weg, der den Fokus hielt, und er fällt auf
 * `document.body`. Ein Listener am Canvas-ELEMENT sähe von dort nichts mehr
 * (body ist sein Vorfahr, nicht sein Nachfahre), und ausgerechnet das
 * Rückgängig, das man nach einem Löschen am dringendsten braucht, käme nie
 * an. Genau so gemeldet.
 *
 * Der Riegel von `useCanvasDuplicateShortcut` — nur am Canvas horchen —
 * schützt dort vor dem ⌘D der **Aufgabenliste**. Für ⌘Z gibt es einen eigenen:
 * Seit F-Q1 nimmt die Taste auch in den **Listen** etwas zurück (`lib/undo`).
 * Geteilt wird sie nach der **zuletzt berührten Hälfte** (ADR 0028) — nicht
 * danach, ob die Pinnwand offen ist: Wer links in der Liste etwas löscht und
 * ⌘Z drückt, meint die Liste, auch wenn rechts das Brett steht. Beim Schließen
 * gibt die Pinnwand die Taste an die Liste zurück.
 *
 * Das ist **nicht** die Fokus-Prüfung von unten in neuer Gestalt: Gemessen
 * wird nicht, wo der Fokus LIEGT, sondern wo zuletzt geklickt oder getippt
 * WURDE. Ein gelöschter Zettel ändert das nicht.
 *
 * **Zwei Wege hinein, und der zweite ist unter Electron der einzige, der
 * trägt:**
 *
 * | | |
 * |---|---|
 * | Web | der Tastendruck am `window` |
 * | Electron | die Ereignisse aus `useUndoRouter` |
 *
 * Unter Electron verbraucht das native Menü das ⌘Z, bevor der Renderer es
 * sieht — beim Drücken leuchtet „Bearbeiten" auf, und der Tastendruck kommt
 * nie an. `menuService.ts` schickt die Nachricht deshalb herüber, und
 * `useUndoRouter` entscheidet app-weit, wem sie gilt: dem Textfeld oder dem
 * Canvas. Was hier ankommt, ist bereits für den Canvas bestimmt.
 *
 * Der Tastendruck-Weg bleibt trotzdem: Im Web gibt es kein Menü, das etwas
 * verbrauchte.
 *
 * **Ohne Fokus-Prüfung.** Ein erster Anlauf ließ den Canvas nur ran, solange
 * der Fokus bei ihm oder bei niemandem lag — das ging schief: Nach einem
 * Löschen fällt der Fokus nicht verlässlich auf `document.body`, er kann auf
 * jedem umgebenden Element der Projektseite landen. Der Riegel schützte auch
 * vor nichts: ⌘Z ist im Renderer sonst nirgends belegt, und der Hook läuft
 * ohnehin nur, solange der Canvas im sichtbaren Tab offen ist.
 *
 * **Im Bearbeiten-Modus gehört ⌘Z dem Textfeld.** Wer gerade tippt, will seine
 * letzte Eingabe zurück, nicht die Notiz von vorhin. Das Ereignis läuft dort
 * unangetastet weiter, und Chromiums eigenes Text-Rückgängig erledigt den
 * Rest.
 *
 * **Kein ⌘Y fürs Wiederherstellen.** ⇧⌘Z genügt, und auf einem deutschen
 * Layout liegt Y dort, wo andere Z erwarten — ein Kürzel, das je nach Tastatur
 * etwas anderes bedeutet, ist keins.
 *
 * Aufgehalten wird nur, was wir auch verarbeiten: Bei jeder anderen Taste (und
 * bei ⌘Z im Text einer Notiz) läuft das Ereignis weiter.
 *
 * @example
 * useCanvasHistoryShortcut({ enabled, onUndo, onRedo })
 */
export function useCanvasHistoryShortcut({
  enabled,
  onUndo,
  onRedo,
}: UseCanvasHistoryShortcutOptions) {
  // Über Refs, damit der Listener nicht bei jeder Änderung am Stapel neu
  // gebunden wird.
  const onUndoRef = useRef(onUndo)
  onUndoRef.current = onUndo
  const onRedoRef = useRef(onRedo)
  onRedoRef.current = onRedo

  useEffect(() => {
    // Kein Warten auf React Flows Pane: Der Hook braucht sie nicht mehr, und
    // ein `domNode`, das noch nicht steht, wäre nur ein weiterer Grund, aus
    // dem ⌘Z stillschweigend nichts täte.
    if (!enabled) return

    // Aufziehen ist hingehen (siehe oben).
    touchUndoRegion('board')

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return
      // ⌥⌘Z gehört uns nicht.
      if (e.altKey) return
      // `code` statt `key`, damit das Kürzel auf jedem Layout dort liegt, wo
      // es eingezeichnet ist — auf einem deutschen wäre `key` sonst „y".
      if (e.code !== 'KeyZ') return
      // Im Text einer Notiz gehört ⌘Z der Eingabe (siehe oben).
      const tag = (e.target as HTMLElement | null)?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA') return
      // Die Tasten der **Aufgaben-Details** gehören ihnen (CONTEXT.md): Bei
      // geteiltem Canvas liegen sie als Slide-over darüber, und der Fokus steht
      // dort meist auf einem KNOPF — am INPUT/TEXTAREA-Wächter oben vorbei.
      if (isInsideTaskDetails(e.target)) return
      // Einem offenen Menü seine Tasten lassen.
      if (document.querySelector('[role="menu"]')) return
      // Zuletzt in der Liste geklickt? Dann meint ⌘Z sie (siehe oben).
      if (lastTouchedUndoRegion() !== 'board') return

      e.preventDefault()
      e.stopPropagation()
      if (e.shiftKey) onRedoRef.current()
      else onUndoRef.current()
    }

    const handleUndoEvent = () => { if (lastTouchedUndoRegion() === 'board') onUndoRef.current() }
    const handleRedoEvent = () => { if (lastTouchedUndoRegion() === 'board') onRedoRef.current() }

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener(APP_UNDO_EVENT, handleUndoEvent)
    window.addEventListener(APP_REDO_EVENT, handleRedoEvent)
    return () => {
      // Zu heisst: Die Taste gehört wieder der Liste.
      resetUndoRegion()
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener(APP_UNDO_EVENT, handleUndoEvent)
      window.removeEventListener(APP_REDO_EVENT, handleRedoEvent)
    }
  }, [enabled])
}
